import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, UserPlus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectCurrentClinicId } from '../../../store/slices/authSlice';
import type { Api } from '../../../types/generated';

interface FormData {
  name: string;
  email: string;
  phone: string;
  roles: ('Owner' | 'Manager' | 'Prescriber' | 'Staff')[];
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  roles?: string;
}

const validateForm = (data: FormData): ValidationErrors => {
  const errors: ValidationErrors = {};

  // Required field validation
  if (!data.name?.trim()) errors.name = 'Name is required';
  if (!data.email?.trim()) errors.email = 'Email is required';
  if (!data.phone?.trim()) errors.phone = 'Phone number is required';
  if (!data.roles || data.roles.length === 0) errors.roles = 'At least one role is required';

  // Format validation
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate phone number has exactly 10 digits
  const phoneDigits = data.phone.replace(/\D/g, '');
  if (data.phone && phoneDigits.length !== 10) {
    errors.phone = 'Phone number must be exactly 10 digits';
  }

  return errors;
};

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Api.User) => Promise<{ id: string; href: string }>;
  loading?: boolean;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  loading = false
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    roles: ['Staff']
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const currentClinicId = useSelector(selectCurrentClinicId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[CreateUserModal] Form submitted', { formData });
    
    setIsSubmitting(true);

    // Validate form data
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    console.log('[CreateUserModal] Validation errors:', validationErrors);

    // If there are validation errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      console.log('[CreateUserModal] Validation failed, not submitting');
      setIsSubmitting(false);
      return;
    }

    const createData: Api.User = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.replace(/\D/g, ''), // Send only digits to the API
      roles: formData.roles
    } as Api.User;

    console.log('[CreateUserModal] Calling onCreate with data:', createData);

    try {
      await onCreate(createData);
      
      console.log('[CreateUserModal] User created successfully');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        roles: ['Staff']
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to create user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Limit to 10 digits
    const trimmed = digits.substring(0, 10);

    // Format as (XXX) XXX-XXXX
    if (trimmed.length === 0) return '';
    if (trimmed.length <= 3) return `(${trimmed}`;
    if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
    return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    // Apply phone formatting if it's the phone field
    const formattedValue = field === 'phone' ? formatPhoneNumber(value) : value;
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRoleChange = (role: 'Owner' | 'Manager' | 'Prescriber' | 'Staff', checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
    // Clear role error when user makes a selection
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: undefined }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-syne font-medium text-vitl-navy flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
              <UserPlus className="w-6 h-6" />
            </div>
            <div>
              <div>Add New User</div>
              <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                Create a new user account for the clinic
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">
              User Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                  className={`mt-1 ${errors.name ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="John Doe"
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  className={`mt-1 ${errors.email ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="john@example.com"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number * <span className="text-xs font-normal text-gray-500">(10 digits)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
                  className={`mt-1 ${errors.phone ? 'border-red-500' : 'focus:border-emerald-400'}`}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  required
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Roles * <span className="text-xs font-normal text-gray-500">(Select all that apply)</span>
                </Label>
                <div className={`mt-2 space-y-3 p-3 border rounded-md ${errors.roles ? 'border-red-500' : 'border-gray-300'}`}>
                  {[
                    { value: 'Staff', description: 'Can view patients and basic information' },
                    { value: 'Prescriber', description: 'Can create and manage orders' },
                    { value: 'Manager', description: 'Can manage users and clinic settings' },
                    { value: 'Owner', description: 'Has full access to all features' }
                  ].map((role) => (
                    <div key={role.value} className="flex items-start space-x-3">
                      <div className="flex items-center h-5">
                        <input
                          id={`role-${role.value}`}
                          type="checkbox"
                          checked={formData.roles.includes(role.value as any)}
                          onChange={(e) => handleRoleChange(role.value as any, e.target.checked)}
                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={`role-${role.value}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                          {role.value}
                        </label>
                        <p className="text-xs text-gray-500">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.roles && (
                  <p className="mt-1 text-sm text-red-600">{errors.roles}</p>
                )}
              </div>
            </div>
          </div>

          {/* Onboarding Information */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-emerald-800 mb-2">Account Setup</h4>
            <p className="text-sm text-emerald-700">
              The user will receive an email invitation to set up their password and complete their profile. 
              They'll be able to access the clinic dashboard once they accept the invitation.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-emerald-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading || isSubmitting}
              className="border-gray-300 hover:border-emerald-400"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white"
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating User...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;